/**
 * Types for the Archive Person journey (backend: PersonArchiveService).
 */

export type ArchiveCategoryId =
  | 'assets'
  | 'business_systems'
  | 'owned_procedures'
  | 'manager_of'
  | 'raci'
  | 'service_desk_admin'
  | 'yard_manager';

export type ArchiveAction = 'unassign' | 'remove' | 'transfer' | 'reassign';

export type RaciRoleLabel = 'Responsible' | 'Accountable';

export type PartyType = 'User' | 'JobRole' | 'ExternalPartner';

export const TERMINATION_REASONS = ['Termination', 'Redundancy', 'Resignation'] as const;
export type TerminationReason = (typeof TERMINATION_REASONS)[number];

/** Ids the resolve call needs for one dependency row. */
export interface ArchiveItemMeta {
  assetId?: number;
  assignmentId?: number;
  businessSystemId?: number;
  procedureId?: number;
  reportUserId?: number;
  activityId?: number;
  role?: RaciRoleLabel;
  deskId?: number;
  yardId?: number;
  memberId?: number;
}

export interface ArchiveDependencyItem {
  /** Stable id for React keys and picker state. */
  id: string;
  /** Display values, already formatted server-side for this category's columns. */
  values: string[];
  meta: ArchiveItemMeta;
}

export interface ArchiveCategory {
  id: ArchiveCategoryId;
  label: string;
  subtitle: string;
  columns: string[];
  action: ArchiveAction;
  picker: boolean;
  pickerLabel?: string;
  empty: string;
  count: number;
  resolved: boolean;
  badge: string;
  items: ArchiveDependencyItem[];
}

export interface ArchiveDependenciesResponse {
  success: boolean;
  totals: { items: number; categories: number };
  categories: ArchiveCategory[];
}

export interface ArchiveAttachment {
  key: string;
  name: string;
  size: number;
  /** Short-lived signed URL, only present on responses from the API. */
  url?: string;
}

export interface ArchiveDraft {
  terminationDate: string | null;
  terminationReason: TerminationReason | string | null;
  attachment: ArchiveAttachment | null;
  startedBy: { id: number; name: string } | null;
  startedAt: string;
  savedAt: string;
}

export interface ArchiveEvent {
  id: number;
  category: ArchiveCategoryId;
  text: string;
  actor: { id: number; name: string } | null;
  createdAt: string;
}

export interface ArchiveDraftResponse {
  success: boolean;
  draft: ArchiveDraft | null;
  events: ArchiveEvent[];
}

/** The subset of the person record the journey needs. */
export interface ArchivePerson {
  id: number | string;
  name: string;
  email?: string;
  jobRole?: string;
  userRole?: string;
  department?: string;
  archived?: boolean;
  archiveDraft?: ArchiveDraft | null;
}

export interface ArchivePersonResponse {
  success: boolean;
  user: ArchivePerson;
}

/**
 * A person as returned by `GET /api/users/selection` — every active, non-archived
 * person regardless of the caller's page permissions (plus their permission
 * strings, which is how desk-administrator eligibility is derived).
 */
export interface ArchiveSelectablePerson extends ArchivePerson {
  permissions?: string[];
}

export interface ArchiveSelectablePeopleResponse {
  success: boolean;
  users: ArchiveSelectablePerson[];
}

/** Whoever takes a responsibility over. R/A RACI slots also accept roles/partners. */
export interface ArchiveReplacement {
  id: string;
  name: string;
  type: PartyType;
}

export interface ArchiveEventPayload {
  category: ArchiveCategoryId;
  itemId: number;
  role?: RaciRoleLabel;
  replacementId?: number;
  replacementType?: PartyType;
}

export interface ArchiveEventResponse {
  success: boolean;
  event: ArchiveEvent;
}
